import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { alpha, useTheme } from '@mui/material/styles';
import {
  employerVerificationAnalyticsApi,
  type EmployerRollupDto,
} from '../api/employerVerificationAnalyticsApi';
import { messageFromEmployerVerificationHttpError } from '../lib/employerVerificationHttpErrorMessage';

function defaultRange(): { verifiedFrom: string; verifiedTo: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 90 * 24 * 60 * 60 * 1000);
  return { verifiedFrom: from.toISOString(), verifiedTo: to.toISOString() };
}

function formatIso(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

function SummaryCards({
  outcomes,
  gateFailures,
}: {
  outcomes: Array<{ finalStatusBucket: string; count: number }>;
  gateFailures: {
    existenceFail: number;
    linkageFail: number;
    legitimacyFail: number;
    structuralFail: number;
    temporalFail: number;
  };
}) {
  const theme = useTheme();

  const topOutcomes = useMemo(() => [...outcomes].slice(0, 6), [outcomes]);
  const totalVerifications =
    outcomes.reduce((s, o) => s + (Number.isFinite(o.count) ? o.count : 0), 0) ||
    1;

  return (
    <>
      <Grid container spacing={1.75} sx={{ mb: 2 }}>
        {topOutcomes.map((o) => {
          const pct = Math.round((100 * o.count) / totalVerifications);
          return (
            <Grid key={o.finalStatusBucket} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, letterSpacing: 0.04 }}
                  >
                    Final outcome bucket
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 900 }}>
                    {o.finalStatusBucket}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>{o.count}</strong> ({pct}%)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Gate-level FAIL sums (within date window)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Rows where each persisted gate outcome equals FAIL. One verification can
            contribute multiple gate counts.
          </Typography>
          <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
            {(
              [
                ['Existence', gateFailures.existenceFail],
                ['Linkage', gateFailures.linkageFail],
                ['Legitimacy', gateFailures.legitimacyFail],
                ['Structural', gateFailures.structuralFail],
                ['Temporal', gateFailures.temporalFail],
              ] as const
            ).map(([label, n]) => (
              <Box
                key={label}
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {n}
                </Typography>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}

function MultiAttemptRow({
  applicationId,
  verificationRowCount,
  firstVerifiedAt,
  lastVerifiedAt,
  fraudSignals,
}: {
  applicationId: string;
  verificationRowCount: number;
  firstVerifiedAt: string | null;
  lastVerifiedAt: string | null;
  fraudSignals: Array<{
    id: string;
    signalName: string;
    riskContributionText: string;
    observationPreview: string | null;
    observedAt: string | null;
    verificationId: string | null;
  }>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ '& > td': { borderBottom: open ? 0 : undefined } }}>
        <TableCell width={48}>
          <IconButton
            aria-label={open ? 'collapse' : 'expand'}
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>
          {applicationId}
        </TableCell>
        <TableCell align="right">{verificationRowCount}</TableCell>
        <TableCell>{formatIso(firstVerifiedAt)}</TableCell>
        <TableCell>{formatIso(lastVerifiedAt)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 1, pb: 1.75, pt: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Fraud signals linked by <code>observationData.verificationId</code> (
                {fraudSignals.length})
              </Typography>
              {fraudSignals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No fraud rows matched these verification ids in this batch.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Signal</TableCell>
                      <TableCell>Risk</TableCell>
                      <TableCell>Verification</TableCell>
                      <TableCell>Observed</TableCell>
                      <TableCell>Observation (preview)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fraudSignals.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell sx={{ fontWeight: 700 }}>{f.signalName}</TableCell>
                        <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>
                          {f.riskContributionText}
                        </TableCell>
                        <TableCell
                          sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}
                        >
                          {f.verificationId ?? '—'}
                        </TableCell>
                        <TableCell>{formatIso(f.observedAt)}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 420,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {f.observationPreview ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function EmployerVerificationAnalyticsDashboard() {
  const theme = useTheme();
  const [range, setRange] = useState(defaultRange);
  const [draft, setDraft] = useState(range);
  const [employerPage, setEmployerPage] = useState(0);
  const [employerRows, setEmployerRows] = useState(25);
  const [employerName, setEmployerName] = useState('');
  const [employerNameDraft, setEmployerNameDraft] = useState('');
  const [multiPage, setMultiPage] = useState(0);
  const [multiRows, setMultiRows] = useState(25);
  const [employerDetailId, setEmployerDetailId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const entityIdRaw = p.get('entityId')?.trim();
      const vf = p.get('verifiedFrom')?.trim();
      const vt = p.get('verifiedTo')?.trim();
      if (vf && vt) {
        const nextRange = { verifiedFrom: vf, verifiedTo: vt };
        setRange(nextRange);
        setDraft(nextRange);
      }
      if (entityIdRaw) {
        setEmployerDetailId(entityIdRaw);
        requestAnimationFrame(() =>
          document.getElementById('ev-employers-rollups-section')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          }),
        );
      }
    } catch {
      // ignore malformed query
    }
  }, []);

  const summaryQ = useQuery({
    queryKey: ['ems-analytics-summary', range],
    queryFn: () => employerVerificationAnalyticsApi.summary(range),
  });

  const multiQ = useQuery({
    queryKey: ['ems-analytics-multi', range, multiPage, multiRows],
    queryFn: () =>
      employerVerificationAnalyticsApi.multiAttemptApplications(
        range,
        multiRows,
        multiPage * multiRows,
      ),
  });

  const employersQ = useQuery({
    queryKey: [
      'ems-analytics-employers',
      range,
      employerPage,
      employerRows,
      employerName,
    ],
    queryFn: () =>
      employerVerificationAnalyticsApi.employerRollups(range, {
        limit: employerRows,
        offset: employerPage * employerRows,
        employerNameContains: employerName || undefined,
      }),
  });

  const detailQ = useQuery({
    queryKey: ['ems-analytics-employer-detail', employerDetailId, range],
    queryFn: () => employerVerificationAnalyticsApi.employerDetail(employerDetailId!, range),
    enabled: Boolean(employerDetailId),
  });

  const applyRange = () => setRange(draft);
  const applyEmployerFilter = () => {
    setEmployerName(employerNameDraft);
    setEmployerPage(0);
  };

  const err =
    (summaryQ.error && messageFromEmployerVerificationHttpError(summaryQ.error)) ||
    (multiQ.error && messageFromEmployerVerificationHttpError(multiQ.error)) ||
    (employersQ.error && messageFromEmployerVerificationHttpError(employersQ.error)) ||
    (detailQ.error && messageFromEmployerVerificationHttpError(detailQ.error));

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
        EMS analytics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only aggregates from <code>Doctrine2Verification</code> with optional
        employer intelligence, locations, and fraud joins. All metrics are scoped to{' '}
        <strong>verifiedAt</strong> within the chosen window (defaults mirror the API:
        trailing 90 days when omitted server-side — here pre-filled below).
      </Typography>

      {err ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      ) : null}

      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
          <Typography sx={{ fontWeight: 800 }}>Date window & filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.25}
            sx={{ mb: 1.25, alignItems: { sm: 'center' } }}
          >
            <TextField
              label="verifiedFrom (ISO)"
              size="small"
              fullWidth
              value={draft.verifiedFrom}
              onChange={(e) => setDraft((d) => ({ ...d, verifiedFrom: e.target.value }))}
              helperText="Start of verifiedAt filter (inclusive)"
            />
            <TextField
              label="verifiedTo (ISO)"
              size="small"
              fullWidth
              value={draft.verifiedTo}
              onChange={(e) => setDraft((d) => ({ ...d, verifiedTo: e.target.value }))}
              helperText="End of verifiedAt filter (inclusive)"
            />
            <Button variant="contained" onClick={applyRange} sx={{ flexShrink: 0 }}>
              Apply range
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Effective range in responses:{' '}
            <strong>
              {summaryQ.data?.dateRange.verifiedFrom
                ? `${formatIso(summaryQ.data.dateRange.verifiedFrom)} → ${formatIso(
                    summaryQ.data.dateRange.verifiedTo,
                  )}`
                : '—'}
            </strong>
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
        Outcomes & gate failures
      </Typography>
      {summaryQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : summaryQ.data ? (
        <>
          <SummaryCards
            outcomes={summaryQ.data.outcomes}
            gateFailures={summaryQ.data.gateFailures}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {summaryQ.data.meta.googlePlaceHint}
          </Typography>
        </>
      ) : null}

      <Typography variant="h6" sx={{ fontWeight: 900, mb: 1, mt: 2 }}>
        Multi-attempt applications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Application ids that produced more than one <code>Doctrine2Verification</code>{' '}
        row inside the window. Fraud rows are bundled per page (verification ids capped
        server-side).
      </Typography>

      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
              <TableCell />
              <TableCell>applicationId</TableCell>
              <TableCell align="right">Verification rows</TableCell>
              <TableCell>First verified</TableCell>
              <TableCell>Last verified</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {multiQ.isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : null}
            {!multiQ.isLoading &&
              multiQ.data?.items.map((row) => (
                <MultiAttemptRow key={row.applicationId} {...row} />
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={multiQ.data?.total ?? 0}
          page={multiPage}
          onPageChange={(_e, p) => setMultiPage(p)}
          rowsPerPage={multiRows}
          onRowsPerPageChange={(e) => {
            setMultiRows(Number(e.target.value));
            setMultiPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Card>

      <Typography id="ev-employers-rollups-section" variant="h6" sx={{ fontWeight: 900, mb: 1, mt: 3 }}>
        Employers (rollup)
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mb: 1.25, alignItems: { sm: 'center' } }}
      >
        <TextField
          label="Legal name contains"
          size="small"
          value={employerNameDraft}
          onChange={(e) => setEmployerNameDraft(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <Button variant="outlined" onClick={applyEmployerFilter}>
          Search
        </Button>
      </Stack>

      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
              <TableCell>Employer</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Verified</TableCell>
              <TableCell align="right">Rejected</TableCell>
              <TableCell align="right">Manual</TableCell>
              <TableCell align="right">Other</TableCell>
              <TableCell>Last activity</TableCell>
              <TableCell>Primary location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employersQ.isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : null}
            {!employersQ.isLoading &&
              employersQ.data?.items.map((r: EmployerRollupDto) => (
                <TableRow
                  key={r.entityId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setEmployerDetailId(r.entityId)}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 800 }}>{r.legalName ?? '—'}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'ui-monospace, monospace' }}
                    >
                      {r.entityId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{r.totalVerifications}</TableCell>
                  <TableCell align="right">{r.verifiedCount}</TableCell>
                  <TableCell align="right">{r.rejectedCount}</TableCell>
                  <TableCell align="right">{r.manualReviewCount}</TableCell>
                  <TableCell align="right">{r.unknownCount}</TableCell>
                  <TableCell>{formatIso(r.lastVerifiedAt)}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={r.normalizedAddressPrimary ?? ''}>
                      {r.normalizedAddressPrimary ?? '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[r.cityPrimary, r.provincePrimary].filter(Boolean).join(', ') || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={employersQ.data?.total ?? 0}
          page={employerPage}
          onPageChange={(_e, p) => setEmployerPage(p)}
          rowsPerPage={employerRows}
          onRowsPerPageChange={(e) => {
            setEmployerRows(Number(e.target.value));
            setEmployerPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Card>

      <Drawer
        anchor="right"
        open={Boolean(employerDetailId)}
        onClose={() => setEmployerDetailId(null)}
      >
        <Box sx={{ width: { xs: '100vw', sm: 480, md: 560 }, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, pr: 2 }}>
              Employer detail
            </Typography>
            <Button size="small" variant="text" onClick={() => setEmployerDetailId(null)}>
              Close
            </Button>
          </Stack>
          {detailQ.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}
          {detailQ.data ? (
            <Stack spacing={1.75}>
              <Typography variant="body2">
                <strong>{detailQ.data.rollup.legalName ?? 'Unknown'}</strong>
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                {detailQ.data.rollup.entityId}
              </Typography>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography sx={{ fontWeight: 800 }}>
                    Intelligence snapshot (current)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      placeId: {detailQ.data.rollup.placeId ?? '—'}
                    </Typography>
                    <Typography variant="body2">
                      placeName: {detailQ.data.rollup.placeNameIntel ?? '—'}
                    </Typography>
                    <Typography variant="body2">
                      businessStatus: {detailQ.data.rollup.businessStatusIntel ?? '—'}
                    </Typography>
                    <Typography variant="body2">
                      rating: {detailQ.data.rollup.ratingIntel ?? '—'} (
                      {detailQ.data.rollup.userRatingsTotalIntel ?? '—'} ratings)
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography sx={{ fontWeight: 800 }}>Locations (all)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Array.isArray(detailQ.data.locations) &&
                  detailQ.data.locations.length > 0 ? (
                    <Stack spacing={1}>
                      {detailQ.data.locations.map((loc) => (
                        <Card key={loc.locId} variant="outlined">
                          <CardContent sx={{ py: 1.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              {loc.normalizedAddress ?? '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[loc.city, loc.province].filter(Boolean).join(', ')} · lat{' '}
                              {loc.lat ?? '—'} lng {loc.lng ?? '—'} ·{' '}
                              {loc.branchType ?? 'branch —'}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No locations returned.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography sx={{ fontWeight: 800 }}>
                    Google Places cache rawResponse
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {detailQ.data.rawPlaceResponse ? (
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        maxHeight: 360,
                        overflow: 'auto',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.text.primary, 0.04),
                        fontSize: '0.72rem',
                      }}
                    >
                      {JSON.stringify(detailQ.data.rawPlaceResponse, null, 2)}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No cached row matched the current snapshot placeId, or snapshot
                      has no placeId.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Stack>
          ) : null}
        </Box>
      </Drawer>
    </Box>
  );
}
